import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  NavigationItem,
  isFavorited,
  findFavorite,
  getFavoritesBySection as getFavoritesBySectionHelper,
  organizeFavoritesBySection,
  sortFavorites,
} from '../utils/navigationHelpers';
import { useAuth, FavoriteNavigationItem } from './AuthContext';

interface FavoritesContextType {
  favorites: FavoriteNavigationItem[];
  isLoading: boolean;
  error: string | null;
  addFavorite: (item: NavigationItem) => Promise<void>;
  removeFavorite: (itemId: string) => Promise<void>;
  removeFavoriteById: (id: number) => Promise<void>;
  isFavorite: (itemId: string) => boolean;
  getFavorite: (itemId: string) => FavoriteNavigationItem | undefined;
  getFavoritesBySection: (section: string) => FavoriteNavigationItem[];
  getOrganizedFavorites: () => ReturnType<typeof organizeFavoritesBySection>;
  refreshFavorites: () => Promise<void>;
  clearError: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: React.ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteNavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load favorites from user object (already loaded by AuthContext)
  useEffect(() => {
    if (isAuthenticated && user?.favorites) {
      setFavorites(sortFavorites(user.favorites));
    } else if (!isAuthenticated) {
      setFavorites([]);
    }
  }, [user, isAuthenticated]);

  /**
   * Refresh favorites from the server
   */
  const refreshFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavorites([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/navigation/favorites/');
      const fetchedFavorites = response.data as FavoriteNavigationItem[];
      setFavorites(sortFavorites(fetchedFavorites));
    } catch (err: any) {
      console.error('Error fetching favorites:', err);
      setError(err.response?.data?.error || 'Failed to load favorites');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Add a favorite
   */
  const addFavorite = useCallback(
    async (item: NavigationItem) => {
      if (!isAuthenticated) {
        setError('You must be logged in to add favorites');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Calculate next order value (place at end)
        const maxOrder = favorites.length > 0
          ? Math.max(...favorites.map(f => f.order))
          : 0;

        const payload = {
          item_id: item.id,
          name: item.name,
          route: item.route,
          icon: item.icon || '',
          section: item.section,
          category: item.category || '',
          order: maxOrder + 1,
        };

        const response = await axios.post('/api/navigation/favorites/', payload);
        const newFavorite = response.data as FavoriteNavigationItem;

        // Add to local state
        setFavorites((prev) => sortFavorites([...prev, newFavorite]));
      } catch (err: any) {
        console.error('Error adding favorite:', err);
        const errorMessage = err.response?.data?.item_id?.[0] ||
                            err.response?.data?.error ||
                            'Failed to add favorite';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, favorites]
  );

  /**
   * Remove a favorite by database ID
   */
  const removeFavoriteById = useCallback(
    async (id: number) => {
      if (!isAuthenticated) {
        setError('You must be logged in to remove favorites');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        await axios.delete(`/api/navigation/favorites/${id}/`);

        // Remove from local state
        setFavorites((prev) => prev.filter((fav) => fav.id !== id));
      } catch (err: any) {
        console.error('Error removing favorite:', err);
        const errorMessage = err.response?.data?.error || 'Failed to remove favorite';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated]
  );

  /**
   * Remove a favorite by item_id
   */
  const removeFavorite = useCallback(
    async (itemId: string) => {
      if (!isAuthenticated) {
        setError('You must be logged in to remove favorites');
        return;
      }

      const favorite = findFavorite(itemId, favorites);
      if (!favorite) {
        setError('Favorite not found');
        return;
      }

      await removeFavoriteById(favorite.id);
    },
    [isAuthenticated, favorites, removeFavoriteById]
  );

  /**
   * Check if an item is favorited
   */
  const isFavoriteFunc = useCallback(
    (itemId: string): boolean => {
      return isFavorited(itemId, favorites);
    },
    [favorites]
  );

  /**
   * Get a favorite by item_id
   */
  const getFavorite = useCallback(
    (itemId: string): FavoriteNavigationItem | undefined => {
      return findFavorite(itemId, favorites);
    },
    [favorites]
  );

  /**
   * Get favorites for a specific section
   */
  const getFavoritesBySection = useCallback(
    (section: string): FavoriteNavigationItem[] => {
      return getFavoritesBySectionHelper(section, favorites);
    },
    [favorites]
  );

  /**
   * Get favorites organized by section and category
   */
  const getOrganizedFavorites = useCallback(() => {
    return organizeFavoritesBySection(favorites);
  }, [favorites]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: FavoritesContextType = {
    favorites,
    isLoading,
    error,
    addFavorite,
    removeFavorite,
    removeFavoriteById,
    isFavorite: isFavoriteFunc,
    getFavorite,
    getFavoritesBySection,
    getOrganizedFavorites,
    refreshFavorites,
    clearError,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
